import { request } from "$/lib/api"
import addonOrderQueries from "$/lib/queries/addon-order-queries.ts"
import { deliveryQueries } from "$/lib/queries/delivery-queries"
import jobQueries from "$/lib/queries/job-queries"
import workOrderQueries from "$/lib/queries/work-order-queries"
import {
  AddProductToAction,
  CombinedReceipt,
  Receipt,
  ReplaceWorkOrderContents,
  Route,
  UpdateProductArticleNumber,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

async function fetchReceipt(jobId: string): Promise<Receipt> {
  return await request({
    url: populateRoute(Route.RECEIPT__GET_AS_MECHANIC, jobId),
    method: "GET",
  })
}

async function fetchCombinedReceipt(
  storeId: string,
  jobIds: string[],
): Promise<CombinedReceipt> {
  return await request({
    url: populateRoute(Route.RECEIPT__GET_COMBINED, storeId),
    method: "POST",
    data: { jobIds },
  })
}

const receiptQueries = {
  all: () => ["receipt"],
  detail: (jobId: string) =>
    queryOptions({
      queryKey: [...receiptQueries.all(), "detail", jobId],
      queryFn: () => fetchReceipt(jobId),
    }),
  combined: (storeId: string, jobIds: string[]) =>
    queryOptions({
      queryKey: [...receiptQueries.all(), "combined", storeId, ...jobIds],
      queryFn: () => fetchCombinedReceipt(storeId, jobIds),
    }),
}

export const useAddProductToAction = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      jobId,
      productData,
    }: { jobId: string; productData: AddProductToAction }) =>
      request({
        url: populateRoute(Route.WORK_ORDER__ADD_PRODUCT_TO_ACTION, jobId),
        method: "POST",
        data: productData,
      }),
    onSuccess: (_, { jobId }) => {
      // Invalidate and refetch all receipt-related data. Adding a product
      // with needsOrdering=true creates a delivery and updates the job's
      // totals on the backend, so invalidate those caches as well.
      queryClient.invalidateQueries({
        queryKey: receiptQueries.all(),
      })
      queryClient.invalidateQueries({
        queryKey: receiptQueries.detail(jobId).queryKey,
      })
      queryClient.invalidateQueries({
        queryKey: addonOrderQueries.detail(jobId).queryKey,
      })
      queryClient.invalidateQueries({
        queryKey: workOrderQueries.detail(jobId).queryKey,
      })
      queryClient.invalidateQueries({
        queryKey: deliveryQueries.byJobId(jobId).queryKey,
      })
      queryClient.invalidateQueries({
        queryKey: jobQueries.detail(jobId).queryKey,
      })
    },
  })
}

export const useUpdateProductArticleNumber = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      jobId,
      productId,
      data,
    }: {
      jobId: string
      productId: string
      data: UpdateProductArticleNumber
    }) =>
      request({
        url: populateRoute(
          Route.WORK_ORDER__UPDATE_PRODUCT_ARTICLE_NUMBER,
          jobId,
          productId,
        ),
        method: "PATCH",
        data,
      }),
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({
        queryKey: receiptQueries.all(),
      })
      queryClient.invalidateQueries({
        queryKey: receiptQueries.detail(jobId).queryKey,
      })
    },
  })
}

export const useDeleteProductFromAction = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ jobId, productId }: { jobId: string; productId: string }) =>
      request({
        url: populateRoute(
          Route.WORK_ORDER__DELETE_PRODUCT_FROM_ACTION,
          jobId,
          productId,
        ),
        method: "DELETE",
      }),
    onSuccess: (_, { jobId }) => {
      // Invalidate and refetch all receipt-related data
      queryClient.invalidateQueries({
        queryKey: receiptQueries.all(),
      })
      queryClient.invalidateQueries({
        queryKey: receiptQueries.detail(jobId).queryKey,
      })
      queryClient.invalidateQueries({
        queryKey: addonOrderQueries.detail(jobId).queryKey,
      })
      queryClient.invalidateQueries({
        queryKey: workOrderQueries.detail(jobId).queryKey,
      })
    },
  })
}

export const useReplaceWorkOrderContents = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      jobId,
      contents,
    }: { jobId: string; contents: ReplaceWorkOrderContents }) =>
      request({
        url: populateRoute(Route.WORK_ORDER__REPLACE_CONTENTS, jobId),
        method: "PUT",
        data: contents,
      }),
    onSuccess: async (_, { jobId }) => {
      // Await the refetches so callers (e.g. the receipt edit form) can
      // re-enter edit mode immediately after save without racing against
      // the suspense-query refetch on the freshly-invalidated data.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: receiptQueries.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: receiptQueries.detail(jobId).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: addonOrderQueries.detail(jobId).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: workOrderQueries.detail(jobId).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: deliveryQueries.byJobId(jobId).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: jobQueries.detail(jobId).queryKey,
        }),
      ])
    },
  })
}

export default receiptQueries
